import { AppSelect } from '~/components/new/app-ui/app-select'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { resolveTabs } from '~/components/new/app-ui/app-tabs-state'
import { ScrollArea, ScrollAreaContent } from '~/components/ui/scroll-area'
import { useActiveToursSelectorOptions } from '~/lib/logistics-operations-query'
import { cn } from '~/lib/utils'
import type {
  LogisticsSearch,
  LogisticsTabValue,
} from '~/routes/_authenticated/logistics/-search-schema'
import { usePermissionGuard } from '~/utils/rbac'
import { useAuthorizedTabs } from '~/utils/rbacTabs'
import {
  LOGISTICS_FILTER_CHIP,
  LOGISTICS_NAV_BAR_ONE_LINE,
  LOGISTICS_NAV_FILTERS_ONE_LINE,
  LOGISTICS_NAV_TABS_SCROLL,
  LOGISTICS_SELECT_CONTENT_CLASS,
} from './logistics-styles'
import { kitchenDepartmentFiltersOf } from './logistics-filter-options'
import { logisticsTabDataNeeds } from './logistics-tabs-state'
import { activeToursQueryOf } from './operations-list'
import { LOGISTICS_TABS, LOGISTICS_TOUR_READ } from './tabs'
import { TourLoadTabLabel, useTourLoadAlerts } from './tour-load-checks-ui'

/** Onglets résolus contre les droits (cf. `resolveTabs`). */
export const useLogisticsTabs = (requestedTab: LogisticsTabValue) =>
  resolveTabs(LOGISTICS_TABS, useAuthorizedTabs(LOGISTICS_TABS), requestedTab)

// Largeur bornée, valeur tronquée : un nom de cuisine centrale est libre, et sur une ligne les
// filtres ne rétrécissent pas (cf. `LOGISTICS_NAV_FILTERS_ONE_LINE`) ; les onglets paieraient
// l'excédent.
const logisticsSelectClassName = cn(
  LOGISTICS_FILTER_CHIP,
  'max-w-60 data-placeholder:text-primary-600 [&>span]:block [&>span]:min-w-0 [&>span]:truncate',
)

/**
 * Barre de la page Logistique (maquette) : onglets du module, puis les filtres de la Gestion
 * opérationnelle (sans la Période) ; ceux du Simulateur sont dans son panneau liste. Commune à la
 * liste et au détail d'une tournée, où elle mène à la liste (`onSearchChange` décide de la
 * navigation).
 */
export function LogisticsNavBar({
  search,
  onSearchChange,
}: {
  search: LogisticsSearch
  onSearchChange: (patch: Partial<LogisticsSearch>) => void
}) {
  const { tabs, activeTab } = useLogisticsTabs(search.tab)
  // Pastille de la Gestion opérationnelle : la tournée validée la plus en alerte sur les 3 semaines
  // à venir (#982), visible depuis tous les onglets.
  const canReadTours = usePermissionGuard(LOGISTICS_TOUR_READ).authorized
  const loadLevel = useTourLoadAlerts(canReadTours).alerts[0]?.level
  const tabsWithLoad =
    loadLevel === undefined
      ? tabs
      : tabs.map((tab) =>
          tab.value === 'operations'
            ? { ...tab, label: <TourLoadTabLabel label={tab.label} level={loadLevel} /> }
            : tab,
        )
  const showFilters = logisticsTabDataNeeds(activeTab).navBarFilters
  // Options des sélecteurs dans la réponse de la liste, sous son propre droit de lecture ; son
  // erreur s'affiche dans le panneau (cf. `useActiveToursSelectorOptions`).
  const optionsQuery = useActiveToursSelectorOptions(activeToursQueryOf(search), showFilters)
  const filters = kitchenDepartmentFiltersOf(optionsQuery.data, optionsQuery.status, search)

  // Une ligne dès que la barre est assez large : sa largeur réelle, pas celle de l'écran (cf.
  // `LOGISTICS_NAV_BAR_ONE_LINE`).
  return (
    <div className="@container">
      <div
        className={cn(
          'mb-2 flex flex-wrap items-center gap-4 rounded-3xl border border-black/6 bg-background-100 p-2',
          LOGISTICS_NAV_BAR_ONE_LINE,
        )}
      >
        <ScrollArea scrollFade className={LOGISTICS_NAV_TABS_SCROLL}>
          <ScrollAreaContent>
            <AppTabs
              tabs={tabsWithLoad}
              variant="pill"
              value={activeTab}
              onTabChange={(tab) => onSearchChange({ tab })}
            />
          </ScrollAreaContent>
        </ScrollArea>

        {showFilters && (
          <div
            className={cn(
              'flex flex-wrap items-center gap-2 self-stretch border-l border-black/6 pl-4',
              LOGISTICS_NAV_FILTERS_ONE_LINE,
            )}
          >
            <AppSelect
              placeholder="Cuisines centrales"
              placeholderOptionLabel="Toutes les cuisines centrales"
              contentClassName={LOGISTICS_SELECT_CONTENT_CLASS}
              options={filters.kitchen.options}
              value={filters.kitchen.value}
              onChange={(value) => onSearchChange({ kitchen: value ?? undefined })}
              className={logisticsSelectClassName}
              valueTitle
            />
            <AppSelect
              placeholder="Départements"
              placeholderOptionLabel="Tous les départements"
              contentClassName={LOGISTICS_SELECT_CONTENT_CLASS}
              options={filters.department.options}
              value={filters.department.value}
              onChange={(value) => onSearchChange({ department: value ?? undefined })}
              className={logisticsSelectClassName}
              valueTitle
            />
          </div>
        )}
      </div>
    </div>
  )
}
